import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api.js";
import { useAsync } from "../lib/hooks.js";
import {
  Callout,
  Card,
  DefinitionList,
  Empty,
  Field,
  Loading,
  PageIntro,
  Steps,
  formatNumber,
  useToast,
} from "../components/ui.jsx";
import { IconCheck, IconChevronLeft, IconChevronRight, IconLayers } from "../components/icons.jsx";
import { kindLabel } from "./Dashboard.jsx";

const STEPS = ["Choose a model", "Define the run", "Review and create"];

/**
 * Production runs are staged deliberately.
 *
 * A run writes thousands of permanent identifiers in one transaction and is
 * all or nothing: if any unit collides, none is created. Showing the exact
 * numbers that will be produced before committing is what stops a mistyped
 * prefix from consuming a block of the number space.
 */
export default function Production() {
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [modelCode, setModelCode] = useState("");
  const [prefix, setPrefix] = useState("");
  const [start, setStart] = useState(1);
  const [count, setCount] = useState(50);
  const [timeZone, setTimeZone] = useState("UTC");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const models = useAsync(() => api.listModels(), []);
  const tenants = useAsync(() => api.listTenants().catch(() => []), []);

  const model = (models.data ?? []).find((m) => m.Code === modelCode);

  // The number layout is derived from the configured length so the preview
  // matches exactly what the server will generate.
  const NUMBER_LENGTH = 11;
  const seqWidth = NUMBER_LENGTH - prefix.length;
  const capacity = seqWidth > 0 ? 10 ** seqWidth : 0;
  const overflows = Number(start) + Number(count) > capacity;
  const prefixValid = /^[0-9]*$/.test(prefix) && seqWidth >= 1;

  const preview = useMemo(() => {
    if (!prefixValid || overflows || !count) return [];
    const first = String(Number(start)).padStart(seqWidth, "0");
    const last = String(Number(start) + Number(count) - 1).padStart(seqWidth, "0");
    return [prefix + first, prefix + last];
  }, [prefix, start, count, seqWidth, prefixValid, overflows]);

  const canDefine = Boolean(model);
  const canReview =
    canDefine && prefixValid && !overflows && Number(count) >= 1 && Number(count) <= 5000;

  async function create() {
    setBusy(true);
    try {
      const res = await api.registerBatch({
        ModelCode: modelCode,
        NumberPrefix: prefix,
        StartSequence: Number(start),
        Count: Number(count),
        TimeZone: timeZone,
      });
      setResult(res);
      toast.success(`${res.Created} devices registered`, "Every unit in the run was created.");
    } catch (err) {
      // An all-or-nothing run means nothing was written, and saying so
      // explicitly stops an operator from hunting for a partial batch.
      toast.error("Nothing was created", err.message);
    } finally {
      setBusy(false);
    }
  }

  function restart() {
    setResult(null);
    setStep(0);
    setPrefix("");
    setStart(1);
    setCount(50);
  }

  if (models.loading) return <Loading label="Loading hardware models" />;

  if ((models.data ?? []).length === 0) {
    return (
      <Card>
        <Empty
          title="No hardware models yet"
          action={
            <Link className="btn btn-primary" to="/models">
              Register a model first
            </Link>
          }
        >
          A production run builds units from a model, so at least one must exist.
        </Empty>
      </Card>
    );
  }

  if (result) {
    return (
      <>
        <PageIntro title="Production run complete">
          Every unit was created. The next step is to issue them to a tenant, after which
          they can be commissioned in the field.
        </PageIntro>

        <Card
          title={`${result.Created} devices registered`}
          hint={`${result.Entries[0]?.Number} through ${result.Entries[result.Entries.length - 1]?.Number}`}
          footer={
            <>
              <button className="btn btn-secondary" onClick={restart}>
                Start another run
              </button>
              <IssueToTenant
                numbers={result.Entries.map((e) => e.Number)}
                tenants={tenants.data ?? []}
              />
            </>
          }
        >
          <Callout kind="success">
            All {result.Created} units exist. A run is all or nothing, so there is no
            partial batch to reconcile.
          </Callout>

          <div className="table-wrap" style={{ maxHeight: 320, overflowY: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Device number</th>
                  <th>Serial</th>
                </tr>
              </thead>
              <tbody>
                {result.Entries.slice(0, 100).map((e) => (
                  <tr key={e.Number}>
                    <td className="mono">{e.Number}</td>
                    <td className="mono hint">{e.Serial}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result.Entries.length > 100 && (
            <p className="hint" style={{ marginTop: 10 }}>
              Showing the first 100 of {result.Entries.length}.
            </p>
          )}
        </Card>
      </>
    );
  }

  return (
    <>
      <PageIntro title="Register a production run">
        This creates a contiguous block of device numbers from one hardware model. The run
        is written in a single transaction: if any number in the range is already taken,
        nothing at all is created and you can correct the range and retry safely.
      </PageIntro>

      <Steps steps={STEPS} current={step} />

      {step === 0 && (
        <Card
          title="Which hardware is being built?"
          hint="The model supplies channels, phases and the rated load"
          footer={
            <button className="btn btn-primary" disabled={!canDefine} onClick={() => setStep(1)}>
              Continue <IconChevronRight />
            </button>
          }
        >
          <div className="grid grid-2">
            {(models.data ?? []).map((m) => (
              <button
                key={m.Code}
                type="button"
                onClick={() => setModelCode(m.Code)}
                className="card"
                style={{
                  textAlign: "left",
                  padding: 16,
                  cursor: "pointer",
                  borderColor: modelCode === m.Code ? "var(--brand-indigo)" : "var(--border)",
                  background: modelCode === m.Code ? "var(--brand-indigo-100)" : "var(--surface)",
                  fontFamily: "inherit",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <strong className="mono">{m.Code}</strong>
                  {modelCode === m.Code && <IconCheck style={{ color: "var(--brand-indigo)" }} />}
                </div>
                <div className="hint" style={{ marginTop: 6 }}>
                  {kindLabel(m.Kind)} ·{" "}
                  {m.Kind === "NOVALINK"
                    ? `carries ${m.GatewayCapacity} devices`
                    : `${m.ChannelCount > 1 ? `${m.ChannelCount}-in-1` : "single channel"}, ${
                        m.Phases === 3 ? "three phase" : "single phase"
                      }`}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {step === 1 && (
        <Card
          title="Define the number range"
          hint="Device numbers are eleven digits: a prefix you choose plus a sequence"
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setStep(0)}>
                <IconChevronLeft /> Back
              </button>
              <button className="btn btn-primary" disabled={!canReview} onClick={() => setStep(2)}>
                Review <IconChevronRight />
              </button>
            </>
          }
        >
          <div className="field-row">
            <Field
              label="Number prefix"
              hint={
                prefixValid
                  ? `${seqWidth} digits remain for the sequence, allowing ${formatNumber(capacity, 0)} units.`
                  : "The prefix is too long; no digits are left for the sequence."
              }
              error={!/^[0-9]*$/.test(prefix) ? "Digits only." : null}
            >
              <input
                className="input mono"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="e.g. 2601"
              />
            </Field>

            <Field label="Time zone" hint="Applied to every unit in the run.">
              <input
                className="input"
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
                placeholder="UTC"
              />
            </Field>
          </div>

          <div className="field-row">
            <Field
              label="Start sequence"
              required
              hint="Continue where the previous run stopped to keep numbers contiguous."
            >
              <input
                className="input"
                type="number"
                min="0"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </Field>

            <Field label="How many units?" required hint="Up to 5,000 in one run.">
              <input
                className="input"
                type="number"
                min="1"
                max="5000"
                value={count}
                onChange={(e) => setCount(e.target.value)}
              />
            </Field>
          </div>

          {overflows && (
            <Callout kind="warning">
              This range does not fit. With a {prefix.length}-digit prefix only{" "}
              {formatNumber(capacity, 0)} sequence values exist, and you have asked for
              numbers up to {Number(start) + Number(count) - 1}. Shorten the prefix or
              reduce the count.
            </Callout>
          )}

          {preview.length === 2 && (
            <Callout kind="info">
              This run will produce <strong className="mono">{preview[0]}</strong> through{" "}
              <strong className="mono">{preview[1]}</strong>.
            </Callout>
          )}
        </Card>
      )}

      {step === 2 && (
        <Card
          title="Review before creating"
          hint="Nothing has been written yet"
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setStep(1)} disabled={busy}>
                <IconChevronLeft /> Back
              </button>
              <button className="btn btn-primary" onClick={create} disabled={busy}>
                {busy ? <span className="spinner" /> : <IconLayers />}
                {busy ? "Creating" : `Create ${count} devices`}
              </button>
            </>
          }
        >
          <DefinitionList
            items={[
              ["Hardware model", <span className="mono">{model?.Code}</span>],
              ["Product kind", kindLabel(model?.Kind)],
              [
                "Channels per unit",
                model?.Kind === "NOVALINK"
                  ? "None. A gateway meters nothing"
                  : model?.ChannelCount > 1
                  ? `${model.ChannelCount} (n-in-1)`
                  : "1",
              ],
              ["Units to create", formatNumber(count, 0)],
              ["First number", <span className="mono">{preview[0]}</span>],
              ["Last number", <span className="mono">{preview[1]}</span>],
              ["Time zone", timeZone || "UTC"],
              [
                "Channel rows",
                model?.Kind === "NOVALINK"
                  ? "0"
                  : formatNumber(Number(count) * (model?.ChannelCount ?? 1), 0),
              ],
            ]}
          />

          <Callout kind="warning">
            Device numbers are permanent. They are printed on the unit and feed token key
            derivation, so a number cannot be reassigned once created. Check the range
            above carefully.
          </Callout>
        </Card>
      )}
    </>
  );
}

/** Issues a completed run to a tenant in one action. */
function IssueToTenant({ numbers, tenants }) {
  const toast = useToast();
  const [alias, setAlias] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <span className="badge badge-success">
        Issued to {alias}
      </span>
    );
  }

  async function issue() {
    setBusy(true);
    try {
      const res = await api.issueBatch({ tenant_alias: alias, numbers });
      setDone(true);
      toast.success(`${res.Created} devices issued`, `Allocated to ${alias}.`);
    } catch (err) {
      toast.error("Nothing was issued", err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <select
        className="select"
        style={{ width: "auto" }}
        value={alias}
        onChange={(e) => setAlias(e.target.value)}
      >
        <option value="">Issue to a tenant…</option>
        {tenants.map((t) => (
          <option key={t.Alias} value={t.Alias}>
            {t.Name}
          </option>
        ))}
      </select>
      <button className="btn btn-primary" disabled={!alias || busy} onClick={issue}>
        {busy ? <span className="spinner" /> : null}
        Issue all {numbers.length}
      </button>
    </div>
  );
}
